import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bed, Bath, Ruler, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getRecentlyViewed } from '@/lib/recentlyViewed';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice } from '@/lib/formatPrice';
import { HorizontalCardRow, HorizontalListingCard, formatTimeAgo } from './HorizontalListingCard';
import { CompactContactActions } from '@/components/property/CompactContactActions';

export function ViewedPropertiesRow() {
  const ids = getRecentlyViewed();
  const navigate = useNavigate();
  const { displayCurrency, rate } = useCurrency();

  const { data: properties } = useQuery({
    queryKey: ['landing-viewed', ids.join(',')],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from('properties')
        .select('*, property_images(url, is_primary)')
        .in('id', ids);
      const map = new Map((data || []).map((p: any) => [p.id, p]));
      return ids.map(id => map.get(id)).filter(Boolean);
    },
  });

  if (!ids.length || !properties?.length) return null;

  return (
    <HorizontalCardRow title="Viewed Properties">
      {properties.map((p: any) => {
        const title = typeof p.title === 'object' ? p.title?.en || '' : p.title || '';
        const img = p.property_images?.find((i: any) => i.is_primary)?.url || p.property_images?.[0]?.url;
        const listingLabel = `${(p.property_type || 'Property').replace(/_/g,' ')} For ${p.listing_type === 'rent' ? 'Rent' : 'Sale'}`;
        const icons: Array<{ icon: React.ReactNode; label: string | number }> = [];
        if (p.area_sqm != null) icons.push({ icon: <Ruler className="h-3 w-3" />, label: `${p.area_sqm} m²` });
        if (p.bedrooms != null) icons.push({ icon: <Bed className="h-3 w-3" />, label: p.bedrooms });
        if (p.bathrooms != null) icons.push({ icon: <Bath className="h-3 w-3" />, label: p.bathrooms });
        return (
          <HorizontalListingCard
            key={p.id}
            imageUrl={img}
            hot={!!p.is_featured}
            topRightChip={formatTimeAgo(p.created_at)}
            priceLabel={formatPrice(p.price, p.currency || 'ETB', displayCurrency, rate)}
            title={`${p.city}${p.sub_city ? `, ${p.sub_city}` : ''}`}
            subtitleLink={listingLabel}
            icons={icons}
            agencyLogoUrl={p.agency_logo_url}
            agencyName={p.agency_name}
            onClick={() => navigate(`/property/${(p as any).slug || p.id}`)}
            onSubtitleClick={() => navigate(`/property/${(p as any).slug || p.id}`)}
            footer={
              <div className="pt-2 mt-1 border-t">
                <CompactContactActions
                  phone={p.contact_phone}
                  agentName={p.contact_person}
                  agencyName={p.agency_name}
                  agentLogo={p.agency_logo_url}
                  referenceId={p.reference_code || `ID${String(p.id).slice(0,8)}`}
                  propertyId={p.id}
                  listingType="property"
                />
              </div>
            }
          />
        );
      })}
    </HorizontalCardRow>
  );
}
